import { all, fork, debounce, spawn, call } from 'redux-saga/effects';
import { notification } from 'antd';
import i18n from '../i18n';
import {
  DEBOUNCED_FETCH_OFFICE_HOLIDAYS_ME,
  DEBOUNCE_ABSENCES_ME,
  fetchAbsencesMe,
  fetchOfficeHolidaysFromSearch,
} from '../modules/absences/actions';
import { ReduxAction } from '../models/Redux';
import { DateTimeString, OfficeId } from '../models/Types';
import {
  DEBOUNCE_TIMERECORDS_ME,
  fetchMyTimeRecords,
} from '../modules/timeRecords/actions';
import {
  DEBOUNCE_MONTHLY_CLOSE_ME,
  fetchMonthlyCloseMe,
} from '../modules/timeKeeping/actions';
import { dispatchSaga } from '../util/sagas';

function* debounceAbsencesMe(
  action: ReduxAction<{
    from?: DateTimeString;
    to?: DateTimeString;
  }>
) {
  try {
    const {
      meta: { from, to },
    } = action;
    yield spawn(dispatchSaga, fetchAbsencesMe(from, to));
  } catch (error) {
    console.error('Error in debounceAbsencesMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('absences:errorMessages.fetchOverviewError'),
    });
  }
}
function* debounceTimeRecordsMe(
  action: ReduxAction<{
    startDate?: DateTimeString;
    endDate?: DateTimeString;
  }>
) {
  try {
    const {
      meta: { startDate, endDate },
    } = action;
    yield spawn(dispatchSaga, fetchMyTimeRecords(startDate, endDate));
  } catch (error) {
    console.error('Error in debounceTimeRecordsMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('timeRecords:errorMessages.fetchError'),
    });
  }
}

const DUPLICATE_THRESHOLD = 5000;

let activeAbortController: AbortController | null = null;
let lastRequestKey: string | null = null;
let lastRequestTime = 0;

function* debounceMonthlyCloseMe(
  action: ReduxAction<{ from?: DateTimeString; to?: DateTimeString }>
) {
  try {
    const {
      meta: { from, to },
    } = action;

    const requestKey = `${from}_${to}`;
    const now = Date.now();

    if (
      lastRequestKey === requestKey &&
      now - lastRequestTime < DUPLICATE_THRESHOLD
    ) {
      return;
    }

    lastRequestKey = requestKey;
    lastRequestTime = now;

    if (activeAbortController) {
      activeAbortController.abort();
      activeAbortController = null;
    }

    activeAbortController = new AbortController();
    const currentController = activeAbortController;

    try {
      yield call(
        dispatchSaga,
        fetchMonthlyCloseMe(from, to),
        { beforeCommit: action.beforeCommit },
        currentController
      );

      if (activeAbortController === currentController)
        activeAbortController = null;
    } catch (error) {
      if (activeAbortController === currentController)
        activeAbortController = null;

      if (currentController.signal.aborted) {
        return;
      }
    }
  } catch (error) {
    console.error('Error in debounceMonthlyCloseMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('timeKeeping:messages.errorMessages.fetchError'),
    });
  }
}
function* debounceOfficeHolidaysMe(
  action: ReduxAction<{
    officeId: OfficeId;
    from?: DateTimeString;
    to?: DateTimeString;
  }>
) {
  try {
    const {
      meta: { officeId, from, to },
    } = action;

    yield spawn(
      dispatchSaga,
      fetchOfficeHolidaysFromSearch(officeId, from, to)
    );
  } catch (error) {
    console.error('Error in debounceOfficeHolidaysMe', error);
    notification.open({
      message: i18n.t('common:error'),
      description: i18n.t('absences:errorMessages.fetchOfficeHolidaysError'),
    });
  }
}
function* watchDebouncedAbsencesMe() {
  yield debounce(500, DEBOUNCE_ABSENCES_ME, debounceAbsencesMe);
}
function* watchDebouncedTimerecordsMe() {
  yield debounce(500, DEBOUNCE_TIMERECORDS_ME, debounceTimeRecordsMe);
}
function* watchDebouncedMonthlyCloseMe() {
  yield debounce(2000, DEBOUNCE_MONTHLY_CLOSE_ME, debounceMonthlyCloseMe);
}
function* watchDebouncedOfficeHolidaysMe() {
  yield debounce(
    500,
    DEBOUNCED_FETCH_OFFICE_HOLIDAYS_ME,
    debounceOfficeHolidaysMe
  );
}

export default function* watchDebouncedAction() {
  yield all([
    fork(watchDebouncedAbsencesMe),
    fork(watchDebouncedTimerecordsMe),
    fork(watchDebouncedMonthlyCloseMe),
    fork(watchDebouncedOfficeHolidaysMe),
  ]);
}
